import { Location } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';

import {
  BomSource, PreflightCode, PreflightFinding, PreflightHolder, PreflightNumbers, PreflightOrder, PreflightResult, PreflightSeverity
} from 'src/app/shared/document-preflight';
import {
  PREFLIGHT_OPEN_DOCUMENT_TOOLTIP, PreflightArrivalLink, PreflightDocumentTarget, preflightDocumentCommands, preflightHolderTarget,
  preflightLastArrivalLinks
} from 'src/app/shared/document-preflight-links';
import {
  PREFLIGHT_CODE_TEXT, PREFLIGHT_FLAG_TEXT, PREFLIGHT_QUEUE_COLUMNS, PreflightNumberColumn, PreflightQueueView, bomNeedText,
  bomSourceText, preflightBomScopeNote, preflightCellNegative, preflightCellText, preflightColumnsFor, preflightLastArrivalText,
  preflightProductLabel, preflightQueueView, preflightSeverityLabel, preflightSeverityTag
} from 'src/app/shared/document-preflight-text';

/**
 * Modal with the result of the invoice pre-flight check (IPF-3,
 * `docs/PROMPT_invoice-preflight-availability-check.md`). Presentation only: the editor owns the state, the merge into
 * `DOCFLAG` and the stale rule. Shows, per product, the numbers, who holds the stock and the incoming orders, plus the
 * two scope notes ("as of the saved version", and that assemblies are not expanded).
 */
@Component({
  selector: 'app-document-preflight-dlg',
  templateUrl: './document-preflight.component.html',
  styleUrls: ['./document-preflight.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class DocumentPreflightComponent {

  @Input() display: boolean = false;
  /** The result to show; null while nothing has been checked. */
  @Input() result: PreflightResult | null = null;
  /** True when the document was edited after the check: the result no longer counts towards the flag. */
  @Input() stale: boolean = false;
  /** True while the request is running. */
  @Input() loading: boolean = false;
  /** A short message when the request failed. */
  @Input() error: string | null = null;
  /** True when the editor can run the check again right now (the document has no unsaved edits). */
  @Input() canRecheck: boolean = false;

  readonly queueColumns = PREFLIGHT_QUEUE_COLUMNS;
  /** The tooltip of every document link button. */
  readonly openDocumentTooltip = PREFLIGHT_OPEN_DOCUMENT_TOOLTIP;

  constructor(private router: Router, private location: Location) { }

  holderTarget(h: PreflightHolder): PreflightDocumentTarget | null { return preflightHolderTarget(h); }

  /** One list of links per finding, so the template gets the same objects on every change-detection pass. */
  private readonly arrivalLinks = new WeakMap<object, PreflightArrivalLink[]>();
  private readonly noLinks: PreflightArrivalLink[] = [];

  lastArrivalLinks(f: PreflightFinding): PreflightArrivalLink[] {
    const la = f.lastArrival;
    if (!la) { return this.noLinks; }
    let links = this.arrivalLinks.get(la);
    if (!links) { links = preflightLastArrivalLinks(la); this.arrivalLinks.set(la, links); }
    return links;
  }

  /**
   * Opens a document's editor in a new browser tab, the way the document selector's leftmost button does. Hash routing: a path from
   * `Router.serializeUrl()` alone is a plain path Apache has no route for (404); `Location.prepareExternalUrl()` turns it into the `#/...` URL.
   */
  openDocument(target: PreflightDocumentTarget | null): void {
    if (!target) { return; }
    const path = this.router.serializeUrl(this.router.createUrlTree(preflightDocumentCommands(target)));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  /** One view per `orders` array, so the template gets the same object on every change-detection pass. */
  private readonly queueViews = new WeakMap<PreflightOrder[], PreflightQueueView>();

  queueView(f: PreflightFinding): PreflightQueueView {
    let v = this.queueViews.get(f.orders);
    if (!v) { v = preflightQueueView(f.orders); this.queueViews.set(f.orders, v); }
    return v;
  }

  lastArrivalText(f: PreflightFinding): string | null { return preflightLastArrivalText(f.lastArrival); }

  columnsOf(n: PreflightNumbers): PreflightNumberColumn[] { return preflightColumnsFor(n); }

  cellText(c: PreflightNumberColumn, n: PreflightNumbers): string { return preflightCellText(c, n); }

  cellNegative(c: PreflightNumberColumn, n: PreflightNumbers): boolean { return preflightCellNegative(c, n); }

  @Output() displayChange = new EventEmitter<boolean>();
  @Output() recheck = new EventEmitter<void>();

  codeText(code: PreflightCode): string { return PREFLIGHT_CODE_TEXT[code] || code; }

  flagText(flag: string): string { return PREFLIGHT_FLAG_TEXT[flag] || flag; }

  severityLabel(severity: PreflightSeverity): string { return preflightSeverityLabel(severity); }

  severityTag(severity: PreflightSeverity): 'danger' | 'warning' | 'secondary' { return preflightSeverityTag(severity); }

  productLabel(f: PreflightFinding): string { return preflightProductLabel(f); }

  bomNeed(f: PreflightFinding): string | null { return bomNeedText(f.bom); }

  bomSource(v: BomSource): string { return bomSourceText(v); }

  get bomScopeNote(): string | null { return this.result ? preflightBomScopeNote(this.result.scope) : null; }

  trackFinding(_: number, f: PreflightFinding): number { return f.prodId; }

  onVisibleChange(visible: boolean): void {
    this.display = visible;
    this.displayChange.emit(visible);
  }

  onRecheck(): void {
    this.recheck.emit();
  }
}
